import React, { useState, useEffect, useRef } from 'react';
import Header from '../layout/Header';
import Button from '../common/Button';
import Modal from '../common/Modal';
import Table, { TableRow, TableCell } from '../common/Table';
import { getCoaches, addCoach, updateCoach, deleteCoach, getLocations, bulkDeleteCoaches, generateOfferLetter as generateOfferLetterApi, sendCoachInvitation as sendCoachInvitationApi, getCoachQuizResults } from '../../firebase/firestore';
import { getCourses } from '../../firebase/trainingAPI';
import { collection, doc, getDoc, getDocs, query, serverTimestamp, where, writeBatch } from 'firebase/firestore';
import { db, auth } from '../../firebase/config';
import { sendPasswordResetEmail } from 'firebase/auth';
import { Plus, Edit, Trash2, Search, UserCheck, UserPlus, Trophy, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, CheckCircle, XCircle, Send, Mail, FileText, DollarSign, Briefcase, MapPin, Loader2, BookOpen, UserRound, AlertCircle } from 'lucide-react';
import { Autocomplete } from '../common/AutoComplete';
import { adminTimesheetApi } from '../../firebase/apis/adminTimesheetApi';
import { payrollApi } from '../../firebase/apis/payrollApi';
import CoachAssignments from './CoachAssignments';
import Toast, { useToast } from '../common/Toast';
import dayjs from 'dayjs';
import { normalizeSport } from '../../utils/memberAnalytics';
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

function coachIsPending(c) {
  const approvalStatus = c?.approvalStatus;
  const status = c?.status;
  // Treat as pending if: explicitly pending, empty string, undefined, or null
  const isPendingApproval = approvalStatus === 'pending' || approvalStatus === '' || approvalStatus === undefined || approvalStatus === null;
  const isPendingStatus = status === 'pending' || status === '' || status === undefined || status === null;
  return isPendingApproval || isPendingStatus;
}

function coachIsRejected(c) {
  return c?.approvalStatus === 'rejected' || c?.status === 'rejected';
}

function coachIsPortalSignup(c) {
  return c?.registrationSource === 'coach_portal';
}

const Coaches = () => {
  const [coaches, setCoaches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingCoach, setEditingCoach] = useState(null);
  const [assigningCoach, setAssigningCoach] = useState(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(''); // eslint-disable-next-line
  /** all | pending_portal — surface coach-portal signups waiting for approval */
  const [registrationFilter, setRegistrationFilter] = useState('all');
  const [debugInfo, setDebugInfo] = useState({ totalUsers: 0, coachUsers: 0 });
  const [selectedCoachIds, setSelectedCoachIds] = useState([]);
  const [isAddingCoach, setIsAddingCoach] = useState(false);
  const [showCoachAssignmentsPage, setShowCoachAssignmentsPage] = useState(false);
  const selectAllRef = useRef(null);

  // Timesheet modal state
  const [isTimesheetModalOpen, setIsTimesheetModalOpen] = useState(false);
  const [selectedCoachForTimesheets, setSelectedCoachForTimesheets] = useState(null);
  const [timesheets, setTimesheets] = useState([]);
  const [timesheetLoading, setTimesheetLoading] = useState(false);
  const [timesheetSummary, setTimesheetSummary] = useState(null);
  const [selectedTimesheets, setSelectedTimesheets] = useState([]);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [payrollProfile, setPayrollProfile] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [reviewActionLoading, setReviewActionLoading] = useState(false);

  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [resetSending, setResetSending] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // Approve coach confirmation modal
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [approveCoachTarget, setApproveCoachTarget] = useState(null);

  // NEW: Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [newCoach, setNewCoach] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'coach',
    employmentType: '',
    hourlyPayRate: '',
    assignedLocations: [],
    assignedSports: [],
    coachPosition: '',
    startDate: '',
    registrationSource: 'admin',
    approvalStatus: 'approved',
    status: 'approved',
    isActive: true,
    onboardingComplete: false
  });
  const [showOfferLetter, setShowOfferLetter] = useState(false);
  const [generatedOfferLetter, setGeneratedOfferLetter] = useState(null);
  const [coachJustCreated, setCoachJustCreated] = useState(null);
  const [sendingInvitationId, setSendingInvitationId] = useState(null);
  const [invitationSent, setInvitationSent] = useState(false);
  const [addingAndSending, setAddingAndSending] = useState(false);

  // Course assignment state
  const [allCourses, setAllCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [showCourseAssignment, setShowCourseAssignment] = useState(false);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [courseAssignmentSaving, setCourseAssignmentSaving] = useState(false);
  const [viewingCoachCourses, setViewingCoachCourses] = useState(null);

  // Profile modal state
  const [showProfile, setShowProfile] = useState(false);
  const [profileCoach, setProfileCoach] = useState(null);
  const [activeProfileTab, setActiveProfileTab] = useState('Information');
  const [coachQuizResults, setCoachQuizResults] = useState([]);
  const [selectedQuizCourse, setSelectedQuizCourse] = useState('');
  const [pendingQuizCourse, setPendingQuizCourse] = useState('');
  const [showCourseEditor, setShowCourseEditor] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);

  const { toasts, success, error, removeToast } = useToast();

  // Assignment state - sport-specific
  const [assignmentData, setAssignmentData] = useState({
    primarySport: 'Soccer',
    assignedGroups: [],
    assignedLocations: [],
    secondarySports: []
  });

  const ageGroups = ['3U', '4U', '5U', '6U', '7U', '8U', '9U', '10U', '11U', '12U', '13U', '14U'];
  const sports = [
    'Soccer',
    'Flag Football',
    'Cheer',
    'Basketball'
  ];

  const itemsPerPageOptions = [5, 10, 25, 50];

  useEffect(() => {
    loadData();
    loadCourses();
  }, []);

  // NEW: Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Keep selections in sync with available data
  useEffect(() => {
    setSelectedCoachIds((prev) => prev.filter(id => coaches.some(coach => coach.id === id)));
  }, [coaches]);

  // Sync course selection when Courses tab opens
  useEffect(() => {
    if (activeProfileTab === 'Courses' && profileCoach) {
      setSelectedCourseIds(profileCoach.assignedCourses || []);
    }
  }, [activeProfileTab, profileCoach]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Debug: Check all users first
      const allUsersSnapshot = await getDocs(collection(db, 'users'));
      const allUsers = allUsersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      console.log('🔍 DEBUG: All users in collection:', allUsers);
      console.log('🔍 DEBUG: Users with role coach:', allUsers.filter(u => u.role === 'coach'));

      // Get coaches and locations
      const [coachesData, locationsData] = await Promise.all([
        getCoaches(),
        getLocations()
      ]);
      console.log('✅ Loaded coaches data:', coachesData);
      console.log('✅ Loaded locations data:', locationsData);

      setCoaches(coachesData);
      const processedLocations = locationsData.map(location => {
        if (typeof location === 'string') {
          return location;
        }
        if (location.name) return location.name;
        if (location.city && location.state) return `${location.city}, ${location.state}`;
        if (location.address) return location.address;
        return 'Unknown Location';
      });

      setLocations(processedLocations);

      setDebugInfo({
        totalUsers: allUsers.length,
        coachUsers: coachesData.length
      });
      setSelectedCoachIds([]);

    } catch (error) {
      console.error('❌ Error loading data:', error);

      // Fallback: Try direct query for coaches
      try {
        console.log('🔄 Trying fallback method...');
        const fallbackSnapshot = await getDocs(
          query(collection(db, 'users'), where('role', '==', 'coach'))
        );
        const fallbackCoaches = fallbackSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('🔄 Fallback coaches:', fallbackCoaches);
        setCoaches(fallbackCoaches);
      } catch (fallbackError) {
        console.error('❌ Fallback also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      setCoursesLoading(true);
      const data = await getCourses();
      setAllCourses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load courses:', err);
      setAllCourses([]);
    } finally {
      setCoursesLoading(false);
    }
  };

  const handleAssignCourses = async (coach) => {
    await loadCourses();
    setViewingCoachCourses(coach);
    setSelectedCourseIds(coach.assignedCourses || []);
    setShowCourseAssignment(true);
  };

  const handleSaveCourseAssignment = async () => {
    if (!viewingCoachCourses) return;
    try {
      setCourseAssignmentSaving(true);
      await updateCoach(viewingCoachCourses.id, {
        assignedCourses: selectedCourseIds
      });
      success('Courses assigned successfully!');
      setShowCourseAssignment(false);
      setViewingCoachCourses(null);
      loadData();
    } catch (err) {
      console.error('Failed to assign courses:', err);
      error('Failed to assign courses: ' + err.message);
    } finally {
      setCourseAssignmentSaving(false);
    }
  };

  const handleViewProfile = async (coach) => {
    setProfileCoach(coach);
    setShowProfile(true);
    setActiveProfileTab('Information');
    setProfileLoading(true);
    setCoachQuizResults([]);
    try {
      const results = await getCoachQuizResults(coach.id);
      if (Array.isArray(results)) {
        results.sort((a, b) => new Date(a.attemptedAt || 0) - new Date(b.attemptedAt || 0));
      }
      setCoachQuizResults(results);
    } catch {
      setCoachQuizResults([]);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleCloseProfile = () => {
    setShowProfile(false);
    setProfileCoach(null);
    setCoachQuizResults([]);
  };

  const handleApproveCoach = async (coach) => {
    setApproveCoachTarget(coach);
    setIsApproveModalOpen(true);
  };

  const handleConfirmApproveCoach = async () => {
    const coach = approveCoachTarget;
    if (!coach) return;
    try {
      await updateCoach(coach.id, {
        approvalStatus: 'approved',
        status: 'approved',
        isActive: true
      });
      // Auto-create payroll profile for the coach
      try {
        await payrollApi.autoCreateProfile(coach.id);
      } catch (profileErr) {
        console.warn('Could not auto-create payroll profile:', profileErr);
      }
      loadData();
    } catch (err) {
      console.error('Approve coach failed:', err);
      alert('Failed to approve coach: ' + (err.message || String(err)));
    } finally {
      setIsApproveModalOpen(false);
      setApproveCoachTarget(null);
    }
  };

  const handleRejectCoach = async (coach) => {
    const name = `${coach.firstName || ''} ${coach.lastName || ''}`.trim() || 'this coach';
    if (
      !window.confirm(
        `Reject ${name}? They will not be able to sign in to the coach portal. You can remove their account later if needed.`
      )
    ) {
      return;
    }
    try {
      await updateCoach(coach.id, {
        approvalStatus: 'rejected',
        status: 'rejected',
        isActive: false
      });
      loadData();
    } catch (err) {
      console.error('Reject coach failed:', err);
      alert('Failed to reject coach: ' + (err.message || String(err)));
    }
  };

  const handleAddCoach = async (e, shouldSendInvitation = false) => {
    e.preventDefault();
    try {
      if (shouldSendInvitation) {
        setAddingAndSending(true);
      } else {
        setIsAddingCoach(true);
      }
      const coachPayload = {
        ...newCoach,
        hourlyPayRate: newCoach.employmentType === 'paid' ? parseFloat(newCoach.hourlyPayRate) || 0 : 0,
        registrationSource: 'admin',
        approvalStatus: 'approved',
        status: 'approved',
        isActive: true,
        onboardingComplete: false
      };
      const result = await addCoach(coachPayload);
      const coachId = result?.id || result;

      // Generate offer letter if paid coach
      let offerLetter = null;
      if (newCoach.employmentType === 'paid') {
        try {
          const letterResp = await generateOfferLetterApi(coachId, {
            firstName: newCoach.firstName,
            lastName: newCoach.lastName,
            position: newCoach.coachPosition,
            hourlyRate: parseFloat(newCoach.hourlyPayRate) || 0,
            startDate: newCoach.startDate,
            location: newCoach.assignedLocations?.[0] || ''
          });
          offerLetter = letterResp?.data ?? letterResp;
        } catch (err) {
          console.warn('Offer letter generation failed:', err);
        }
      }

      const createdCoach = { id: coachId, ...coachPayload, offerLetter };

      if (shouldSendInvitation && coachId) {
        try {
          await sendCoachInvitationApi(coachId);
          setInvitationSent(true);
        } catch (err) {
          console.warn('Invitation sending failed:', err);
        }
      }

      setCoachJustCreated(createdCoach);
      if (offerLetter) {
        setGeneratedOfferLetter(offerLetter);
      }

      // Load courses for assignment
      loadCourses();
      setSelectedCourseIds([]);

      resetNewCoachForm();
      loadData();

      if (!shouldSendInvitation) {
        setIsAddModalOpen(false);
      }
    } catch (error) {
      console.error('❌ Error adding coach:', error);
      alert('Error adding coach: ' + error.message);
    } finally {
      setIsAddingCoach(false);
      setAddingAndSending(false);
    }
  };

  const resetNewCoachForm = () => {
    setNewCoach({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: 'coach',
      employmentType: '',
      hourlyPayRate: '',
      assignedLocations: [],
      assignedSports: [],
      coachPosition: '',
      startDate: '',
      registrationSource: 'admin',
      approvalStatus: 'approved',
      status: 'approved',
      isActive: true,
      onboardingComplete: false
    });
  };

  const handleSendInvitation = async (coach) => {
    try {
      setSendingInvitationId(coach.id);
      setInvitationSent(false);
      console.log('📨 [Coaches] Sending invitation for coach:', { id: coach.id, firstName: coach.firstName, lastName: coach.lastName, email: coach.email });
      const result = await sendCoachInvitationApi(coach.id);
      console.log('✅ [Coaches] Invitation sent successfully:', result);
      setInvitationSent(true);
      success('Invitation sent successfully!');
      setTimeout(() => setInvitationSent(false), 4000);
    } catch (err) {
      console.error('❌ [Coaches] Error sending invitation:', err);
      console.error('❌ [Coaches] Coach ID:', coach?.id);
      console.error('❌ [Coaches] Coach email:', coach?.email);
      error('Failed to send invitation: ' + err.message);
    } finally {
      setSendingInvitationId(null);
    }
  };

  const handleEditCoach = async (e) => {
    e.preventDefault();
    try {
      console.log('✏️ Updating coach with data:', editingCoach);
      await updateCoach(editingCoach.id, editingCoach);
      setEditingCoach(null);
      loadData();
    } catch (error) {
      console.error('❌ Error updating coach:', error);
      alert('Error updating coach: ' + error.message);
    }
  };

  const handleSendResetLink = async (user) => {
    try {
      setResetSending(true);
      await sendPasswordResetEmail(auth, user.email);
      setResetSent(true);
      success('Reset link sent successfully!');
      setTimeout(() => {
        setIsResetPasswordModalOpen(false);
        setResetSent(false);
        setResetPasswordUser(null);
      }, 3000);
    } catch (err) {
      console.error('Error sending password reset:', err);
      error('Failed to send reset email: ' + err.message);
    } finally {
      setResetSending(false);
    }
  };

  const handleDeleteCoach = async (id) => {
    if (window.confirm('Are you sure you want to delete this coach?')) {
      try {
        await deleteCoach(id);
        await payrollApi.deleteProfile(id).catch(() => {});
        loadData();
      } catch (error) {
        console.error('❌ Error deleting coach:', error);
        alert('Error deleting coach: ' + error.message);
      }
    }
  };

  const toggleCoachSelection = (coachId) => {
    setSelectedCoachIds((prevSelected) =>
      prevSelected.includes(coachId)
        ? prevSelected.filter(id => id !== coachId)
        : [...prevSelected, coachId]
    );
  };

  const handleSelectAllOnPage = (coachIdsOnPage) => {
    const allSelected = coachIdsOnPage.every(id => selectedCoachIds.includes(id));
    if (allSelected) {
      setSelectedCoachIds(prev => prev.filter(id => !coachIdsOnPage.includes(id)));
    } else {
      const combined = new Set([...selectedCoachIds, ...coachIdsOnPage]);
      setSelectedCoachIds(Array.from(combined));
    }
  };

  const handleBulkDeleteCoaches = async () => {
    if (selectedCoachIds.length === 0) return;

    const confirmMessage = `Are you sure you want to delete ${selectedCoachIds.length} selected coach(es)? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) return;

    try {
      setLoading(true);
      await bulkDeleteCoaches(selectedCoachIds);
      await Promise.allSettled(selectedCoachIds.map(id => payrollApi.deleteProfile(id)));
      setSelectedCoachIds([]);
      await loadData();
    } catch (error) {
      console.error('❌ Error bulk deleting coaches:', error);
      alert('Error deleting selected coaches: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Sport-specific assignment handlers
  const handleAssignCoach = (coach) => {
    setAssigningCoach(coach);
    setAssignmentData({
      primarySport: coach.primarySport || 'Soccer',
      assignedGroups: coach.assignedGroups || [],
      assignedLocations: coach.assignedLocations || [],
      secondarySports: coach.secondarySports || []
    });
    setIsAssignModalOpen(true);
  };

  // CORRECTED: Only assign to existing rosters - no creation
  const handleAssignmentSubmit = async (e) => {
    e.preventDefault();

    if (!assignmentData.assignedGroups.length || !assignmentData.assignedLocations.length) {
      alert('Please select at least one age group and one location.');
      return;
    }

    try {
      console.log('👨‍🏫 Assigning coach to existing rosters only:', assignmentData);

      const batch = writeBatch(db);
      const assignedTeams = [];
      let rostersUpdated = 0;
      let rostersNotFound = 0;

      // For each location + age group combination
      for (const location of assignmentData.assignedLocations) {
        for (const ageGroup of assignmentData.assignedGroups) {
          // Create consistent roster ID format
          const rosterId = `${assignmentData.primarySport.toLowerCase().replace(/\s+/g, '-')}-${ageGroup.toLowerCase()}-${location.toLowerCase().replace(/\s+/g, '-')}`;

          const rosterRef = doc(db, 'rosters', rosterId);
          const rosterSnap = await getDoc(rosterRef);

          if (rosterSnap.exists()) {
            // ONLY update existing roster with coach - don't create new ones
            const existingRoster = rosterSnap.data();

            batch.update(rosterRef, {
              coachId: assigningCoach.id,
              coachName: `${assigningCoach.firstName} ${assigningCoach.lastName}`,
              hasAssignedCoach: true,
              status: existingRoster.hasPlayers ? 'active' : 'needs-players',
              lastUpdated: serverTimestamp(),
              updatedAt: serverTimestamp()
            });

            assignedTeams.push({
              id: rosterId,
              sport: assignmentData.primarySport,
              ageGroup: ageGroup,
              location: location,
              teamName: existingRoster.teamName || `${ageGroup} ${assignmentData.primarySport} - ${location}`,
              isPrimary: true,
              playerCount: existingRoster.playerCount || 0
            });

            rostersUpdated++;
            console.log(`✅ Updated existing roster: ${rosterId} (${existingRoster.playerCount || 0} players)`);

          } else {
            // Just log that roster doesn't exist - don't create it
            rostersNotFound++;
            console.log(`ℹ️ Roster not found (will be created when parents register): ${rosterId}`);
          }
        }
      }

      if (assignedTeams.length === 0) {
        alert(`❌ No existing rosters found for the selected combinations.

📋 Rosters are created automatically when parents register their children.
📍 Selected: ${assignmentData.assignedLocations.join(', ')}
🏆 Sport: ${assignmentData.primarySport}
👶 Age Groups: ${assignmentData.assignedGroups.join(', ')}

ℹ️ Once parents register for these combinations, you can assign this coach to those rosters.`);
        return;
      }

      // Update coach with assigned teams
      const coachRef = doc(db, 'users', assigningCoach.id);
      batch.update(coachRef, {
        primarySport: assignmentData.primarySport,
        secondarySports: assignmentData.secondarySports,
        assignedGroups: assignmentData.assignedGroups,
        assignedLocations: assignmentData.assignedLocations,
        assignedTeams: assignedTeams,
        updatedAt: serverTimestamp()
      });

      await batch.commit();

      setIsAssignModalOpen(false);
      setAssigningCoach(null);
      loadData();

      // Success message with details
      alert(`✅ Coach assigned to existing rosters only!

📊 Results:
- ${rostersUpdated} existing rosters updated with coach
- ${rostersNotFound} combinations had no existing rosters (normal - created when parents register)
- Total active assignments: ${assignedTeams.length}

🎯 Active rosters (with players): ${assignedTeams.filter(t => t.playerCount > 0).length}
⏳ Waiting for players: ${assignedTeams.filter(t => t.playerCount === 0).length}

✨ No new rosters were created - they'll be created automatically when parents register!`);

    } catch (error) {
      console.error('❌ Error assigning coach:', error);
      alert('Error assigning coach: ' + error.message);
    }
  };

  const handleAssignmentAgeGroupToggle = (ageGroup) => {
    const currentGroups = assignmentData.assignedGroups || [];
    const updatedGroups = currentGroups.includes(ageGroup)
      ? currentGroups.filter(group => group !== ageGroup)
      : [...currentGroups, ageGroup];

    setAssignmentData({ ...assignmentData, assignedGroups: updatedGroups });
  };

  // const handleAssignmentLocationToggle = (location) => {
  //   const currentLocations = assignmentData.assignedLocations || [];
  //   const updatedLocations = currentLocations.includes(location)
  //     ? currentLocations.filter(loc => loc !== location)
  //     : [...currentLocations, location];

  //   setAssignmentData({ ...assignmentData, assignedLocations: updatedLocations });
  // };

  const handleSecondarySportToggle = (sport) => {
    if (normalizeSport(sport) === normalizeSport(assignmentData.primarySport)) return;

    const currentSecondary = assignmentData.secondarySports || [];
    const updatedSecondary = currentSecondary.includes(sport)
      ? currentSecondary.filter(s => s !== sport)
      : [...currentSecondary, sport];

    setAssignmentData({ ...assignmentData, secondarySports: updatedSecondary });
  };

  const handleEditCoachSecondarySportToggle = (sport) => {
    if (normalizeSport(sport) === normalizeSport(editingCoach.primarySport)) return;

    const currentSecondary = editingCoach.secondarySports || [];
    const updatedSecondary = currentSecondary.includes(sport)
      ? currentSecondary.filter(s => s !== sport)
      : [...currentSecondary, sport];

    setEditingCoach({ ...editingCoach, secondarySports: updatedSecondary });
  };

  const pendingPortalCount = coaches.filter((c) => coachIsPortalSignup(c) && coachIsPending(c)).length;

  const filteredCoaches = coaches.filter((coach) => {
    const matchesSearch =
      `${coach.firstName || ''} ${coach.lastName || ''}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (coach.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (coach.primarySport || '').toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    if (registrationFilter === 'pending_portal') {
      return coachIsPortalSignup(coach) && coachIsPending(coach);
    }
    return true;
  });

  // NEW: Pagination logic
  const totalItems = filteredCoaches.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredCoaches.slice(startIndex, endIndex);
  const currentPageCoachIds = currentItems.map(coach => coach.id);
  const allCurrentSelected = currentPageCoachIds.length > 0 && currentPageCoachIds.every(id => selectedCoachIds.includes(id));
  const someCurrentSelected = currentPageCoachIds.some(id => selectedCoachIds.includes(id));

  // NEW: Pagination handlers
  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPreviousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const goToPage = (page) => setCurrentPage(page);

  const getPageNumbers = () => {
    const delta = 2;
    const pages = [];
    const startPage = Math.max(1, currentPage - delta);
    const endPage = Math.min(totalPages, currentPage + delta);
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const getSportIcon = (sport) => {
    const icons = {
      'Soccer': '⚽',
      'Basketball': '🏀',
      'Flag Football': '🏈',
      'Cheer': '📣'
    };
    return icons[sport] || '🏆';
  };

  // Fetch timesheets for a coach
  const handleViewTimesheets = async (coach) => {
    try {
      setSelectedCoachForTimesheets(coach);
      setIsTimesheetModalOpen(true);
      setTimesheetLoading(true);
      setSelectedTimesheets([]);
      setPayrollProfile(null);
      setAdminNotes('');

      const [timesheetResponse, profileResult] = await Promise.all([
        adminTimesheetApi.getCoachTimesheets(coach.id, {
          page: 1,
          limit: 100,
        }),
        payrollApi.getProfile(coach.id).catch(() => null)
      ]);

      const responseData = timesheetResponse.data || {};
      const timesheetsArray = responseData.timesheets || [];
      const summaryData = responseData.summary || {};

      setTimesheets(timesheetsArray);
      setTimesheetSummary(summaryData);
      setPayrollProfile(profileResult);
    } catch (error) {
      console.error('Failed to fetch coach timesheets:', error);
      alert('Failed to load timesheets');
      setTimesheets([]);
      setTimesheetSummary(null);
    } finally {
      setTimesheetLoading(false);
    }
  };

  // Handle checkbox selection for timesheets
  const handleSelectTimesheet = (timesheetId) => {
    setSelectedTimesheets((prev) =>
      prev.includes(timesheetId)
        ? prev.filter((id) => id !== timesheetId)
        : [...prev, timesheetId]
    );
  };

  // Handle select all timesheets
  const handleSelectAllTimesheets = () => {
    if (selectedTimesheets.length === timesheets.length) {
      setSelectedTimesheets([]);
    } else {
      setSelectedTimesheets(timesheets.map((ts) => ts.id));
    }
  };

  // Bulk approve handler for timesheets
  const handleBulkApproveTimesheets = async () => {
    if (selectedTimesheets.length === 0) {
      alert("Please select at least one timesheet");
      return;
    }

    try {
      setBulkActionLoading(true);
      const result = await adminTimesheetApi.bulkApproveTimesheets(
        selectedTimesheets,
        "",
        true
      );

      if (result.success) {
        const message = result.message || `Successfully approved ${result.data?.successful || 0} timesheet(s)`;
        alert(message);
        setSelectedTimesheets([]);
        // Refresh timesheets
        if (selectedCoachForTimesheets) {
          const response = await adminTimesheetApi.getCoachTimesheets(selectedCoachForTimesheets.id, {
            page: 1,
            limit: 100,
          });
          const responseData = response.data || {};
          setTimesheets(responseData.timesheets || []);
          setTimesheetSummary(responseData.summary || {});
        }
      } else {
        alert(`Error: ${result.message || "Failed to approve timesheets"}`);
      }
    } catch (error) {
      console.error("Failed to bulk approve timesheets:", error);
      alert(`Failed to approve timesheets: ${error.message}`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk reject handler for timesheets
  const handleBulkRejectTimesheets = async () => {
    if (selectedTimesheets.length === 0) {
      alert("Please select at least one timesheet");
      return;
    }

    if (!rejectionReason || rejectionReason.trim() === "") {
      alert("Please provide a rejection reason");
      return;
    }

    try {
      setBulkActionLoading(true);
      const result = await adminTimesheetApi.bulkRejectTimesheets(
        selectedTimesheets,
        rejectionReason
      );

      if (result.success) {
        const message = result.message || `Successfully rejected ${result.data?.successful || 0} timesheet(s)`;
        alert(message);
        setSelectedTimesheets([]);
        setRejectionReason("");
        setIsRejectModalOpen(false);
        // Refresh timesheets
        if (selectedCoachForTimesheets) {
          const response = await adminTimesheetApi.getCoachTimesheets(selectedCoachForTimesheets.id, {
            page: 1,
            limit: 100,
          });
          const responseData = response.data || {};
          setTimesheets(responseData.timesheets || []);
          setTimesheetSummary(responseData.summary || {});
        }
      } else {
        alert(`Error: ${result.message || "Failed to reject timesheets"}`);
      }
    } catch (error) {
      console.error("Failed to bulk reject timesheets:", error);
      alert(`Failed to reject timesheets: ${error.message}`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Individual approve handler for timesheets
  const handleApproveTimesheet = async (timesheetId) => {
    try {
      setActionLoading(timesheetId);
      const result = await adminTimesheetApi.approveTimesheet(timesheetId);
      const d = result.data || {};
      if (selectedCoachForTimesheets) {
        const response = await adminTimesheetApi.getCoachTimesheets(selectedCoachForTimesheets.id, {
          page: 1,
          limit: 100,
        });
        const responseData = response.data || {};
        setTimesheets(responseData.timesheets || []);
        setTimesheetSummary(responseData.summary || {});
      }
      if (d.isVolunteer) {
        alert(`Timesheet approved — ${d.hours || 0} hrs (Volunteer, $0.00)`);
      } else {
        alert(`Timesheet approved — ${d.hours || 0} hrs @ $${(d.rate || 0).toFixed(2)}/hr = $${(d.amountOwed || 0).toFixed(2)}`);
      }
    } catch (error) {
      console.error('Failed to approve timesheet:', error);
      alert(`Failed to approve: ${error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  // Individual reject handler for timesheets
  const handleRejectTimesheet = async (timesheetId) => {
    const reason = prompt("Please enter reason for rejection:");
    if (reason === null) return;
    try {
      setActionLoading(timesheetId);
      await adminTimesheetApi.rejectTimesheet(timesheetId, reason);
      if (selectedCoachForTimesheets) {
        const response = await adminTimesheetApi.getCoachTimesheets(selectedCoachForTimesheets.id, {
          page: 1,
          limit: 100,
        });
        const responseData = response.data || {};
        setTimesheets(responseData.timesheets || []);
        setTimesheetSummary(responseData.summary || {});
      }
      alert("Timesheet rejected successfully");
    } catch (error) {
      console.error("Failed to reject timesheet:", error);
      alert("Failed to reject timesheet");
    } finally {
      setActionLoading(null);
    }
  };

  const getPayPeriodRange = (entries) => {
    if (!entries?.length) return 'N/A';
    const dates = entries
      .filter(ts => ts.date)
      .map(ts => dayjs.utc(ts.date))
      .filter(d => d.isValid());
    if (!dates.length) return 'N/A';
    let min = dates[0], max = dates[0];
    dates.forEach(d => {
      if (d.isBefore(min)) min = d;
      if (d.isAfter(max)) max = d;
    });
    return `${min.format("MMM D, YYYY")} – ${max.format("MMM D, YYYY")}`;
  };

  const handleApproveAllTimesheets = async () => {
    const submittedIds = timesheets.filter(ts => ts.status === 'submitted').map(ts => ts.id);
    if (submittedIds.length === 0) {
      alert("No submitted timesheets to approve");
      return;
    }
    try {
      setReviewActionLoading(true);
      const result = await adminTimesheetApi.bulkApproveTimesheets(submittedIds, adminNotes, true);
      if (result.success) {
        alert(result.message || `Approved ${submittedIds.length} timesheet(s)`);
        setAdminNotes('');
        if (selectedCoachForTimesheets) {
          const response = await adminTimesheetApi.getCoachTimesheets(selectedCoachForTimesheets.id, { page: 1, limit: 100 });
          const responseData = response.data || {};
          setTimesheets(responseData.timesheets || []);
          setTimesheetSummary(responseData.summary || {});
        }
      } else {
        alert(`Error: ${result.message || "Failed to approve timesheets"}`);
      }
    } catch (error) {
      console.error("Failed to approve timesheets:", error);
      alert(`Failed: ${error.message}`);
    } finally {
      setReviewActionLoading(false);
    }
  };

  const handleOpenRejectModalForReview = () => {
    const submittedIds = timesheets.filter(ts => ts.status === 'submitted').map(ts => ts.id);
    if (submittedIds.length === 0) {
      alert("No submitted timesheets to reject");
      return;
    }
    setIsRejectModalOpen(true);
  };

  const handleRejectAllTimesheets = async () => {
    const submittedIds = timesheets.filter(ts => ts.status === 'submitted').map(ts => ts.id);
    if (submittedIds.length === 0) {
      alert("No submitted timesheets to reject");
      return;
    }
    if (!rejectionReason || rejectionReason.trim() === "") {
      alert("Please provide a rejection reason");
      return;
    }
    try {
      setReviewActionLoading(true);
      const result = await adminTimesheetApi.bulkRejectTimesheets(submittedIds, rejectionReason);
      if (result.success) {
        alert(result.message || `Rejected ${submittedIds.length} timesheet(s)`);
        setRejectionReason("");
        setIsRejectModalOpen(false);
        if (selectedCoachForTimesheets) {
          const response = await adminTimesheetApi.getCoachTimesheets(selectedCoachForTimesheets.id, { page: 1, limit: 100 });
          const responseData = response.data || {};
          setTimesheets(responseData.timesheets || []);
          setTimesheetSummary(responseData.summary || {});
        }
      } else {
        alert(`Error: ${result.message || "Failed to reject timesheets"}`);
      }
    } catch (error) {
      console.error("Failed to reject timesheets:", error);
      alert(`Failed: ${error.message}`);
    } finally {
      setReviewActionLoading(false);
    }
  };

  const handleRequestCorrection = async () => {
    const submittedIds = timesheets.filter(ts => ts.status === 'submitted').map(ts => ts.id);
    if (submittedIds.length === 0) {
      alert("No submitted timesheets to request correction for");
      return;
    }
    const reason = adminNotes.trim() || "Correction requested by admin. Please review and resubmit.";
    try {
      setReviewActionLoading(true);
      const result = await adminTimesheetApi.bulkRejectTimesheets(submittedIds, reason);
      if (result.success) {
        alert(`Correction requested for ${submittedIds.length} timesheet(s)`);
        setAdminNotes('');
        if (selectedCoachForTimesheets) {
          const response = await adminTimesheetApi.getCoachTimesheets(selectedCoachForTimesheets.id, { page: 1, limit: 100 });
          const responseData = response.data || {};
          setTimesheets(responseData.timesheets || []);
          setTimesheetSummary(responseData.summary || {});
        }
      } else {
        alert(`Error: ${result.message || "Failed"}`);
      }
    } catch (error) {
      console.error("Failed:", error);
      alert(`Failed: ${error.message}`);
    } finally {
      setReviewActionLoading(false);
    }
  };

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someCurrentSelected && !allCurrentSelected;
    }
  }, [someCurrentSelected, allCurrentSelected]);

  if (showCoachAssignmentsPage) {
    return (
      <div>
        <div className="mb-4">
          <Button variant="secondary" onClick={() => setShowCoachAssignmentsPage(false)}>
            <ChevronLeft size={18} className="mr-2" />
            Back to Coaches
          </Button>
        </div>
        <CoachAssignments />
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <Header
          title="Coach Management"
          subtitle="Loading coaches..."
        />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading coaches data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header
        title="Coach Management"
        subtitle="Manage all coaches and their sport-specific team assignments"
      />

      <div className="glass rounded-2xl p-4 sm:p-6">
        <div className="mb-6">
          <div className="flex items-start">
            <h3 className="text-lg font-semibold text-gray-800">
              All Coaches ({coaches.length})
              {coaches.length === 0 && (
                <span className="text-red-500 text-sm font-normal ml-2">
                  (No coaches found - check if users have role: 'coach')
                </span>
              )}
            </h3>
          </div>

          <div className="mt-4 flex flex-col gap-3 w-full">
            <div className="flex flex-col md:flex-row md:flex-wrap gap-2 w-full items-stretch md:items-center">
              <div className="relative w-full sm:w-auto">
              <Search size={16} className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search coaches..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full sm:w-[220px] pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <label htmlFor="coach-registration-filter" className="text-xs text-gray-600 whitespace-nowrap">
                  Show:
                </label>
                <select
                  id="coach-registration-filter"
                  value={registrationFilter}
                  onChange={(e) => {
                    setRegistrationFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full sm:w-auto px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 min-w-0 sm:min-w-[200px]"
                >
                  <option value="all">All coaches</option>
                  <option value="pending_portal">
                    Pending coach signups{pendingPortalCount > 0 ? ` (${pendingPortalCount})` : ''}
                  </option>
                </select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 w-full">
              <Button
                onClick={() => setShowCoachAssignmentsPage(true)}
                variant="secondary"
                className="w-full sm:w-auto text-sm py-1.5 px-3 bg-gradient-to-r from-violet-400 to-violet-900 text-white hover:bg-violet-700 border-violet-600"
              >
                <UserCheck size={16} className="mr-1.5" />
                Coach Assignments
              </Button>
              <Button
                onClick={async () => {
                  try {
                    setLoading(true);

                    // Import both sync functions
                    const { syncAllRosters, syncParentsToRosters } = await import('../../firebase/firestore');

                    // Sync rosters first, then parents
                    await syncAllRosters();
                    const parentSyncResult = await syncParentsToRosters();

                    alert(`✅ Complete sync finished!
      
📊 Results:
- All rosters updated with current data
- ${parentSyncResult.parentsUpdated} parents automatically assigned
- ${parentSyncResult.newAssignments} new assignments made

All data is now synchronized!`);

                    loadData();
                  } catch (error) {
                    console.error('❌ Error syncing:', error);
                    alert('Failed to sync: ' + error.message);
                  } finally {
                    setLoading(false);
                  }
                }}
                variant="secondary"
                className="w-full sm:w-auto text-sm py-1.5 px-3 bg-gradient-to-r from-emerald-300 to-emerald-700 text-white hover:bg-emerald-700 border-emerald-600"
              >
                🔄 Sync All Data
              </Button>
              <Button
                onClick={handleBulkDeleteCoaches}
                variant="danger"
                disabled={selectedCoachIds.length === 0}
                className="w-full sm:w-auto min-w-0 sm:min-w-[150px] text-sm py-1.5 px-3"
              >
                <Trash2 size={16} className="mr-1.5" />
                Delete Selected ({selectedCoachIds.length})
              </Button>

              <Button onClick={() => setIsAddModalOpen(true)} className="w-full sm:w-auto text-sm py-1.5 px-3">
                <Plus size={16} className="mr-1.5" />
                Add Coach
              </Button>
            </div>
          </div>
        </div>

        {/* NEW: Pagination controls */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
          <div className="text-sm text-gray-600">
            Showing {currentItems.length} of {totalItems} coaches
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-sm text-gray-600">Show:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="flex-1 sm:flex-none px-3 py-1 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {itemsPerPageOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <span className="text-sm text-gray-600">entries</span>
          </div>
        </div>

        {coaches.length > 0 ? (
          <>
            <Table
              headers={[
                <div className="flex items-center" key="select-all">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    checked={allCurrentSelected}
                    aria-checked={someCurrentSelected && !allCurrentSelected ? 'mixed' : allCurrentSelected}
                    onChange={() => handleSelectAllOnPage(currentPageCoachIds)}
                    className="h-4 w-4 text-primary-600 rounded border-gray-300"
                  />
                </div>,
                'Actions',
                'Name',
                'Email',
                'Phone',
                'Registration',
                'Type',
                'Status',
                'Sport(s)',
                'Teams Assigned',
                'Courses',
                'Location(s)',
                'Position',
                'Joined',
                'Profile'
              ]}
            >
              {currentItems.map((coach) => (
                <TableRow key={coach.id}>
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selectedCoachIds.includes(coach.id)}
                      onChange={() => toggleCoachSelection(coach.id)}
                      className="h-4 w-4 text-primary-600 rounded border-gray-300"
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {/* <button
                        onClick={() => handleViewTimesheets(coach)}
                        className="p-1 text-indigo-600 hover:text-indigo-800"
                        title="View Timesheets"
                      >
                        <Calendar size={16} />
                      </button> */}
                      <button
                        onClick={() => handleAssignCourses(coach)}
                        className="p-1 text-indigo-600 hover:text-indigo-800"
                        title="Assign Courses"
                      >
                        <BookOpen size={16} />
                      </button>
                      <button
                        onClick={() => handleAssignCoach(coach)}
                        className="p-1 text-purple-600 hover:text-purple-800"
                        title="Assign Teams"
                      >
                        <UserPlus size={16} />
                      </button>
                      {coachIsPending(coach) && (
                        <>
                          <button
                            onClick={() => handleApproveCoach(coach)}
                            className="p-1 text-emerald-600 hover:text-emerald-800"
                            title="Approve coach account"
                            type="button"
                          >
                            <UserCheck size={16} />
                          </button>
                          <button
                            onClick={() => handleRejectCoach(coach)}
                            className="p-1 text-rose-600 hover:text-rose-800"
                            title="Reject registration"
                            type="button"
                          >
                            <XCircle size={16} />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => setEditingCoach(coach)}
                        className="p-1 text-blue-600 hover:text-blue-800"
                        title="Edit Coach"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteCoach(coach.id)}
                        className="p-1 text-red-600 hover:text-red-800"
                        title="Delete Coach"
                      >
                        <Trash2 size={16} />
                      </button>
                      {coach.registrationSource === 'admin' && !coach.onboardingComplete && (
                        <button
                          onClick={() => handleSendInvitation(coach)}
                          disabled={sendingInvitationId === coach.id}
                          className="p-1 text-amber-600 hover:text-amber-800 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Send Onboarding Invitation"
                        >
                          {sendingInvitationId === coach.id ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setResetPasswordUser(coach);
                          setIsResetPasswordModalOpen(true);
                          setResetSent(false);
                        }}
                        className="p-1 text-purple-600 hover:text-purple-800"
                        title="Send Password Reset Link"
                      >
                        <Send size={16} />
                      </button>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                        {(coach.firstName || 'C')[0]}{(coach.lastName || 'C')[0]}
                      </div>
                      <div>
                        <div>{coach.firstName || 'N/A'} {coach.lastName || 'N/A'}</div>
                        {coach.yearsExperience && (
                          <div className="text-xs text-gray-500">{coach.yearsExperience} years exp.</div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{coach.email || 'N/A'}</TableCell>
                  <TableCell>
                    <span className="text-sm">{coach.phone || <span className="text-gray-400">—</span>}</span>
                  </TableCell>
                  <TableCell>
                    {coachIsPortalSignup(coach) ? (
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-900 border border-indigo-200"
                        title="Registered from the public coach portal (coach panel signup)"
                      >
                        Coach signup
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500" title="Created from the admin panel">
                        Admin
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {coach.employmentType === 'paid' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                        <DollarSign size={12} className="mr-1" /> Paid
                      </span>
                    ) : coach.employmentType === 'volunteer' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                        Volunteer
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {coachIsPending(coach) ? (
                      <div className="flex flex-col gap-1 items-start">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900 border border-amber-200">
                          Pending approval
                        </span>
                        {coachIsPortalSignup(coach) && (
                          <span className="text-[11px] text-amber-800/90">Awaiting review</span>
                        )}
                      </div>
                    ) : coachIsRejected(coach) ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-900 border border-rose-200">
                        Rejected
                      </span>
                    ) : coach.isActive === false ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                        Inactive
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                        Active
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(coach.assignedSports || (coach.primarySport ? [coach.primarySport] : [])).map((sport) => (
                        <span key={sport} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">
                          {getSportIcon(sport)} {sport}
                        </span>
                      ))}
                      {!coach.assignedSports && !coach.primarySport && (
                        <span className="text-gray-500 text-sm">Not set</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {(coach.assignedTeams || []).slice(0, 2).map((team, index) => (
                        <div key={index} className={`text-xs px-2 py-1 rounded-full ${team.isPrimary
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                          }`}>
                          {team.teamName}
                        </div>
                      ))}
                      {(coach.assignedTeams || []).length > 2 && (
                        <div className="text-xs text-gray-500">
                          +{coach.assignedTeams.length - 2} more teams
                        </div>
                      )}
                      {(!coach.assignedTeams || coach.assignedTeams.length === 0) && (
                        <span className="text-gray-500 text-sm">No teams assigned</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {(coach.assignedCourses || []).length > 0 ? (
                        <>
                          {(coach.assignedCourses || []).slice(0, 2).map((courseId, i) => {
                            const course = allCourses.find((c) => c.id === courseId);
                            return (
                              <div key={i} className="text-xs px-2 py-1 rounded-full bg-indigo-100 text-indigo-800 truncate max-w-[150px]" title={course?.title || courseId}>
                                {course?.title || courseId}
                              </div>
                            );
                          })}
                          {(coach.assignedCourses || []).length > 2 && (
                            <div className="text-xs text-gray-500">
                              +{coach.assignedCourses.length - 2} more
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-500 text-sm">No courses</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {(coach.assignedLocations || (coach.location ? [coach.location] : [])).map((loc, i) => (
                        <div key={i} className="truncate max-w-[150px]" title={loc}>
                          {loc}
                        </div>
                      ))}
                      {(!coach.assignedLocations && !coach.location) && (
                        <span className="text-gray-500">Not specified</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{coach.coachPosition || <span className="text-gray-400">—</span>}</span>
                  </TableCell>
                  <TableCell>
                    {coach.createdAt ? (
                      coach.createdAt.toDate ?
                        coach.createdAt.toDate().toLocaleDateString() :
                        new Date(coach.createdAt).toLocaleDateString()
                    ) : 'N/A'}
                  </TableCell>

                  <TableCell>
                    <button
                      onClick={() => handleViewProfile(coach)}
                      className="inline-flex items-center gap-1 text-sm text-sky-600 hover:text-sky-800 hover:underline transition-colors"
                      title="View Profile"
                    >
                      <UserRound size={16} />
                      <span>Profile</span>
                    </button>
                  </TableCell>

                </TableRow>
              ))}
            </Table>

            {/* NEW: Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mt-6 pt-6 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={goToFirstPage}
                    disabled={currentPage === 1}
                    className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="First Page"
                  >
                    <ChevronsLeft size={16} />
                  </button>
                  <button
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                    className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Previous Page"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <div className="flex items-center gap-1 flex-wrap">
                    {getPageNumbers().map(pageNum => (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${pageNum === currentPage
                          ? 'bg-primary-500 text-white'
                          : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                      >
                        {pageNum}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Next Page"
                  >
                    <ChevronRight size={16} />
                  </button>
                  <button
                    onClick={goToLastPage}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Last Page"
                  >
                    <ChevronsRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <UserCheck size={64} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-600 mb-2">No Coaches Found</h3>
            <div className="text-gray-500 space-y-1">
              <p>No coaches found in the system.</p>
              <p className="text-sm">• Make sure users have role: 'coach' in Firestore</p>
              <p className="text-sm">• Check the 'users' collection</p>
              <p className="text-sm">• Try adding a new coach using the button above</p>
            </div>
          </div>
        )}
      </div>

      {/* All existing modals remain unchanged - keeping them as they are since they work correctly */}
      {/* Sport-Specific Assignment Modal */}
      <Modal
        isOpen={isAssignModalOpen}
        onClose={() => {
          setIsAssignModalOpen(false);
          setAssigningCoach(null);
          setAssignmentData({ primarySport: 'Soccer', assignedGroups: [], assignedLocations: [], secondarySports: [] });
        }}
        title={assigningCoach ? `Assign Teams to ${assigningCoach.firstName} ${assigningCoach.lastName}` : 'Assign Coach to Teams'}
        size="xl"
      >
        {assigningCoach && (
          <form onSubmit={handleAssignmentSubmit} className="space-y-6">
            {/* Coach Info */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Trophy size={20} className="text-purple-600" />
                Coach Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Name:</span>
                  <span className="ml-2 text-gray-600">
                    {assigningCoach.firstName} {assigningCoach.lastName}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Email:</span>
                  <span className="ml-2 text-gray-600">{assigningCoach.email}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Current Primary Sport:</span>
                  <span className="ml-2 text-gray-600 flex items-center gap-1">
                    {getSportIcon(assigningCoach.primarySport)} {assigningCoach.primarySport || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Experience:</span>
                  <span className="ml-2 text-gray-600">{assigningCoach.experience || 'Not specified'}</span>
                </div>
              </div>
            </div>

            {/* Current Team Assignments */}
            {assigningCoach.assignedTeams && assigningCoach.assignedTeams.length > 0 && (
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-3">Current Team Assignments ({assigningCoach.assignedTeams.length})</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {assigningCoach.assignedTeams.map((team, index) => (
                    <div key={index} className={`flex items-center gap-2 p-2 rounded-lg text-sm ${team.isPrimary ? 'bg-blue-200 text-blue-800' : 'bg-green-200 text-green-800'
                      }`}>
                      <span>{getSportIcon(team.sport)}</span>
                      <span>{team.teamName}</span>
                      <span className="text-xs">({team.isPrimary ? 'Primary' : 'Secondary'})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sport Selection */}
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Sport Expertise</h4>

              {/* Primary Sport */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Primary Sport *</label>
                <select
                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none"
                  value={assignmentData.primarySport}
                  onChange={(e) => setAssignmentData({ ...assignmentData, primarySport: e.target.value })}
                  required
                >
                  {sports.map((sport) => (
                    <option key={sport} value={sport}>
                      {getSportIcon(sport)} {sport}
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-600 mt-1">The sport this coach specializes in and will primarily coach</p>
              </div>

              {/* Secondary Sports */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Sports (Optional)</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {sports.filter(sport => normalizeSport(sport) !== normalizeSport(assignmentData.primarySport)).map((sport) => (
                    <button
                      key={sport}
                      type="button"
                      onClick={() => handleSecondarySportToggle(sport)}
                      className={`p-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${assignmentData.secondarySports?.includes(sport)
                        ? 'bg-green-500 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      <span>{getSportIcon(sport)}</span>
                      <span>{sport}</span>
                    </button>
                  ))}
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Additional sports this coach can assist with (limited assignments)
                </p>
              </div>
            </div>

            {/* Age Groups Assignment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Age Groups</label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {ageGroups.map((group) => (
                  <button
                    key={group}
                    type="button"
                    onClick={() => handleAssignmentAgeGroupToggle(group)}
                    className={`p-3 rounded-xl text-center font-medium transition-all ${assignmentData.assignedGroups?.includes(group)
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {group}
                  </button>
                ))}
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Selected: {assignmentData.assignedGroups?.length || 0} age groups
              </p>
            </div>

            {/* Locations Assignment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Locations</label>
              <select
                multiple
                value={assignmentData.assignedLocations}
                onChange={(e) => {
                  const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                  setAssignmentData({
                    ...assignmentData,
                    assignedLocations: selectedOptions
                  });
                }}
                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none"
              >
                {locations.map((loc) => {
                  const label = typeof loc === 'string' ? loc : loc.name || loc.label || loc;
                  return (
                    <option key={label} value={label}>
                      {label}
                    </option>
                  );
                })}
              </select>
              <p className="text-xs text-gray-500 mt-1">Ctrl+click to select multiple locations</p>
            </div>


            {/* Assignment Preview - Updated message */}
            {(assignmentData.assignedGroups?.length > 0 && assignmentData.assignedLocations?.length > 0) && (
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-3">Assignment Preview - Existing Rosters Only</h4>

                {/* Primary Sport Teams */}
                <div className="mb-3">
                  <h5 className="font-medium text-green-700 mb-2">
                    {getSportIcon(assignmentData.primarySport)} {assignmentData.primarySport} Teams (Primary)
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm">
                    {assignmentData.assignedGroups.map(ageGroup =>
                      assignmentData.assignedLocations.map(location => (
                        <div key={`${ageGroup}-${location}`} className="bg-green-200 text-green-800 px-2 py-1 rounded">
                          {ageGroup} {assignmentData.primarySport} - {location}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-green-200">
                  <p className="text-sm text-green-700">
                    <strong>Potential Assignments:</strong> {
                      (assignmentData.assignedGroups?.length || 0) * (assignmentData.assignedLocations?.length || 0)
                    } combinations
                  </p>
                  <div className="text-xs text-green-600 mt-1 space-y-1">
                    <p>✅ <strong>Will assign to:</strong> Existing rosters (created when parents registered)</p>
                    <p>⏭️ <strong>Will skip:</strong> Non-existent rosters (will assign automatically when parents register)</p>
                    <p>🚫 <strong>Will NOT create:</strong> Any new empty rosters</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t">
              <Button
                variant="secondary"
                onClick={() => {
                  setIsAssignModalOpen(false);
                  setAssigningCoach(null);
                  setAssignmentData({ primarySport: 'Soccer', assignedGroups: [], assignedLocations: [], secondarySports: [] });
                }}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                <UserPlus size={16} className="mr-2" />
                Assign to Teams
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Course Assignment Modal */}
      <Modal
        isOpen={showCourseAssignment}
        onClose={() => { setShowCourseAssignment(false); setViewingCoachCourses(null); setSelectedCourseIds([]); }}
        title={viewingCoachCourses ? `Assign Courses to ${viewingCoachCourses.firstName} ${viewingCoachCourses.lastName}` : 'Assign Courses'}
        size="md"
      >
        {viewingCoachCourses && (
          <div className="space-y-5">
            {/* Coach Info */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <BookOpen size={20} className="text-indigo-600" />
                Coach Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Name:</span>
                  <span className="ml-2 text-gray-600">
                    {viewingCoachCourses.firstName} {viewingCoachCourses.lastName}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Email:</span>
                  <span className="ml-2 text-gray-600">{viewingCoachCourses.email}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Current Courses:</span>
                  <span className="ml-2 text-gray-600">
                    {(viewingCoachCourses.assignedCourses || []).length} assigned
                  </span>
                </div>
              </div>
            </div>

            {/* Current Assigned Courses */}
            {(viewingCoachCourses.assignedCourses || []).length > 0 && (
              <div className="bg-indigo-50 rounded-lg p-4">
                <h4 className="font-semibold text-indigo-800 mb-3">
                  Currently Assigned ({viewingCoachCourses.assignedCourses.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {viewingCoachCourses.assignedCourses.map((courseId, i) => {
                    const course = allCourses.find((c) => c.id === courseId);
                    return (
                      <span key={i} className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-indigo-200 text-indigo-800">
                        <BookOpen size={12} className="mr-1" />
                        {course?.title || courseId}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Course Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Training Courses
              </label>
              {coursesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={24} className="animate-spin text-indigo-600" />
                </div>
              ) : allCourses.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <BookOpen size={40} className="mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No courses available. Add courses in Curriculum &gt; Courses first.</p>
                  <Button
                    variant="secondary"
                    onClick={loadCourses}
                    className="mt-3 text-xs"
                  >
                    Refresh
                  </Button>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-xl p-3">
                  {allCourses.map((course) => {
                    const isSelected = selectedCourseIds.includes(course.id);
                    return (
                      <label
                        key={course.id}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all text-sm ${
                          isSelected ? 'bg-indigo-100 border border-indigo-300' : 'bg-white border border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            setSelectedCourseIds((prev) =>
                              isSelected ? prev.filter((id) => id !== course.id) : [...prev, course.id]
                            );
                          }}
                          className="h-4 w-4 text-indigo-600 rounded border-gray-300"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 truncate">{course.title}</span>
                            {course.isRequired && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700 shrink-0">
                                Required
                              </span>
                            )}
                          </div>
                          {course.description && (
                            <p className="text-xs text-gray-500 truncate mt-0.5">{course.description}</p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              {selectedCourseIds.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">{selectedCourseIds.length} course(s) selected</p>
              )}
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t">
              <Button
                variant="secondary"
                onClick={() => { setShowCourseAssignment(false); setViewingCoachCourses(null); setSelectedCourseIds([]); }}
                disabled={courseAssignmentSaving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveCourseAssignment}
                disabled={courseAssignmentSaving}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {courseAssignmentSaving ? (
                  <><Loader2 size={16} className="animate-spin mr-1.5" /> Saving...</>
                ) : (
                  <><BookOpen size={16} className="mr-1.5" /> Save Courses ({selectedCourseIds.length})</>
                )}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Add Coach Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => { setIsAddModalOpen(false); setCoachJustCreated(null); setInvitationSent(false); }}
        title={coachJustCreated ? 'Coach Created Successfully' : 'Add New Coach'}
        size="lg"
      >
        {coachJustCreated ? (
          <div className="space-y-6">
            <div className="text-center py-4">
              <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">
                {coachJustCreated.firstName} {coachJustCreated.lastName}
              </h3>
              <p className="text-gray-500">Coach account created successfully</p>
            </div>

            {/* Offer Letter Preview (if paid) */}
            {generatedOfferLetter && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <h4 className="font-medium text-blue-900">Offer Letter Generated</h4>
                </div>
                <p className="text-sm text-blue-700 mb-3">
                  An offer letter has been generated for this coach based on their pay rate and position.
                </p>
                <Button
                  variant="secondary"
                  onClick={() => setShowOfferLetter(true)}
                  className="text-sm"
                >
                  <FileText size={16} className="mr-1.5" />
                  Preview Offer Letter
                </Button>
              </div>
            )}

            {/* Send Invitation */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="h-5 w-5 text-amber-600" />
                <h4 className="font-medium text-amber-900">Send Invitation</h4>
              </div>
              <p className="text-sm text-amber-700 mb-3">
                Send an onboarding invitation email from <strong>team@yausports.com</strong> to {coachJustCreated.firstName}.
                The email includes a secure, one-time link that expires after <strong>72 hours</strong>.
              </p>
              <ul className="text-xs text-amber-700 mb-3 space-y-1">
                <li>• Link is one-time use — cannot be shared or reused after clicked</li>
                <li>• Expires after 72 hours if not used</li>
                <li>• Coach will set their own password during onboarding</li>
                <li>• You can resend the invitation if the link expires</li>
              </ul>
              {invitationSent ? (
                <div className="flex items-center gap-2 text-green-700 font-medium">
                  <CheckCircle className="h-5 w-5" />
                  Invitation sent to {coachJustCreated.firstName}!
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleSendInvitation(coachJustCreated)}
                    disabled={sendingInvitationId === coachJustCreated?.id}
                  >
                    {sendingInvitationId === coachJustCreated?.id ? (
                      <>Sending...</>
                    ) : (
                      <><Send size={16} className="mr-1.5" /> Send Invitation</>
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Assign Training Courses */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="h-5 w-5 text-indigo-600" />
                <h4 className="font-medium text-indigo-900">Assign Training Courses</h4>
              </div>
              <p className="text-sm text-indigo-700 mb-3">
                Select training courses required for {coachJustCreated.firstName}.
              </p>
              {coursesLoading ? (
                <div className="flex items-center gap-2 text-sm text-indigo-600">
                  <Loader2 size={16} className="animate-spin" />
                  Loading courses...
                </div>
              ) : allCourses.length === 0 ? (
                <div className="text-sm text-indigo-600">
                  No courses available.{' '}
                  <Button
                    variant="secondary"
                    onClick={async () => {
                      await loadCourses();
                      if (allCourses.length === 0) {
                        alert('No training courses found. Go to Curriculum > Courses to add some first.');
                      }
                    }}
                    className="text-xs px-2 py-1"
                  >
                    Refresh
                  </Button>
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {allCourses.map((course) => {
                    const isSelected = selectedCourseIds.includes(course.id);
                    return (
                      <label
                        key={course.id}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all text-sm ${
                          isSelected ? 'bg-indigo-200 text-indigo-900' : 'bg-white text-gray-700 hover:bg-indigo-100'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            setSelectedCourseIds((prev) =>
                              isSelected ? prev.filter((id) => id !== course.id) : [...prev, course.id]
                            );
                          }}
                          className="h-4 w-4 text-indigo-600 rounded border-gray-300"
                        />
                        <div className="flex-1">
                          <span className="font-medium">{course.title}</span>
                          {course.isRequired && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">
                              Required
                            </span>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              <div className="mt-3">
                <Button
                  onClick={async () => {
                    try {
                      if (!coachJustCreated?.id) return;
                      setCourseAssignmentSaving(true);
                      await updateCoach(coachJustCreated.id, {
                        assignedCourses: selectedCourseIds
                      });
                      success('Courses assigned successfully!');
                    } catch (err) {
                      console.error('Failed to assign courses:', err);
                      error('Failed to assign courses: ' + err.message);
                    } finally {
                      setCourseAssignmentSaving(false);
                    }
                  }}
                  disabled={courseAssignmentSaving || selectedCourseIds.length === 0}
                  className="text-sm"
                >
                  {courseAssignmentSaving ? 'Saving...' : `Save Courses (${selectedCourseIds.length} selected)`}
                </Button>
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t">
              <Button onClick={() => { setIsAddModalOpen(false); setCoachJustCreated(null); setInvitationSent(false); setGeneratedOfferLetter(null); setSelectedCourseIds([]); }}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={(e) => handleAddCoach(e, false)} className="space-y-6">
            {/* Row 1: First Name + Last Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:outline-none"
                  value={newCoach.firstName}
                  onChange={(e) => setNewCoach({ ...newCoach, firstName: e.target.value })}
                  required
                  placeholder="Enter first name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:outline-none"
                  value={newCoach.lastName}
                  onChange={(e) => setNewCoach({ ...newCoach, lastName: e.target.value })}
                  required
                  placeholder="Enter last name"
                />
              </div>
            </div>

            {/* Row 2: Email + Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:outline-none"
                  value={newCoach.email}
                  onChange={(e) => setNewCoach({ ...newCoach, email: e.target.value })}
                  required
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:outline-none"
                  value={newCoach.phone}
                  onChange={(e) => setNewCoach({ ...newCoach, phone: e.target.value })}
                  required
                  placeholder="Enter phone number"
                />
              </div>
            </div>

            {/* Row 3: Employment Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Employment Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                {['paid', 'volunteer'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setNewCoach({ ...newCoach, employmentType: type, hourlyPayRate: type === 'volunteer' ? '' : newCoach.hourlyPayRate })}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      newCoach.employmentType === type
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        newCoach.employmentType === type ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {type === 'paid' ? <DollarSign size={20} /> : <Briefcase size={20} />}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 capitalize">{type} Coach</p>
                        <p className="text-sm text-gray-500">
                          {type === 'paid' ? 'Hourly rate required' : 'No pay rate needed'}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Row 4: Hourly Pay Rate (only if Paid) */}
            {newCoach.employmentType === 'paid' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hourly Pay Rate ($) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    className="w-full pl-10 p-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:outline-none"
                    value={newCoach.hourlyPayRate}
                    onChange={(e) => setNewCoach({ ...newCoach, hourlyPayRate: e.target.value })}
                    placeholder="e.g. 25.00"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  This rate will auto-populate the offer letter.
                </p>
              </div>
            )}

            {/* Row 5: Assigned Locations (multi-select) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assigned Location(s) <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {locations.map((loc) => {
                  const label = typeof loc === 'string' ? loc : loc.name || loc.label || loc;
                  const isSelected = newCoach.assignedLocations.includes(label);
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => {
                        const updated = isSelected
                          ? newCoach.assignedLocations.filter(l => l !== label)
                          : [...newCoach.assignedLocations, label];
                        setNewCoach({ ...newCoach, assignedLocations: updated });
                      }}
                      className={`p-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                        isSelected
                          ? 'bg-blue-500 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <MapPin size={14} />
                      <span className="truncate">{label}</span>
                    </button>
                  );
                })}
              </div>
              {newCoach.assignedLocations.length === 0 && (
                <p className="mt-1 text-xs text-amber-600">Please select at least one location</p>
              )}
            </div>

            {/* Row 6: Sports (multi-select) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sport(s) <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {sports.map((sport) => {
                  const isSelected = newCoach.assignedSports.includes(sport);
                  return (
                    <button
                      key={sport}
                      type="button"
                      onClick={() => {
                        const updated = isSelected
                          ? newCoach.assignedSports.filter(s => s !== sport)
                          : [...newCoach.assignedSports, sport];
                        setNewCoach({ ...newCoach, assignedSports: updated });
                      }}
                      className={`p-3 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                        isSelected
                          ? 'bg-green-500 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span>{getSportIcon(sport)}</span>
                      <span>{sport}</span>
                    </button>
                  );
                })}
              </div>
              {newCoach.assignedSports.length === 0 && (
                <p className="mt-1 text-xs text-amber-600">Please select at least one sport</p>
              )}
            </div>

            {/* Row 7: Coach Position */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Coach Position <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:outline-none"
                value={newCoach.coachPosition}
                onChange={(e) => setNewCoach({ ...newCoach, coachPosition: e.target.value })}
                required
                placeholder="e.g. Head Coach, Site Manager, Assistant Coach"
              />
            </div>

            {/* Row 8: Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:outline-none"
                value={newCoach.startDate}
                onChange={(e) => setNewCoach({ ...newCoach, startDate: e.target.value })}
                required
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-4 border-t">
              <Button
                variant="secondary"
                onClick={() => setIsAddModalOpen(false)}
                disabled={isAddingCoach || addingAndSending}
              >
                Cancel
              </Button>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  type="submit"
                  disabled={isAddingCoach || addingAndSending || !newCoach.assignedLocations.length || !newCoach.assignedSports.length || !newCoach.employmentType}
                  variant="secondary"
                >
                  {isAddingCoach ? 'Saving...' : 'Save Coach'}
                </Button>
                <Button
                  type="button"
                  disabled={isAddingCoach || addingAndSending || !newCoach.assignedLocations.length || !newCoach.assignedSports.length || !newCoach.employmentType}
                  onClick={(e) => handleAddCoach(e, true)}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                >
                  {addingAndSending ? (
                    'Saving & Sending...'
                  ) : (
                    <><Send size={16} className="mr-1.5" /> Save & Send Invitation</>
                  )}
                </Button>
              </div>
            </div>
          </form>
        )}
      </Modal>

      {/* Offer Letter Preview Modal */}
      <Modal
        isOpen={showOfferLetter}
        onClose={() => setShowOfferLetter(false)}
        title="Offer Letter Preview"
        size="lg"
      >
        {generatedOfferLetter && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-6 max-h-96 overflow-y-auto">
              {generatedOfferLetter.content || generatedOfferLetter.html ? (
                <div dangerouslySetInnerHTML={{ __html: generatedOfferLetter.content || generatedOfferLetter.html }} />
              ) : (
                <pre className="text-sm text-gray-700 font-sans whitespace-pre-wrap leading-relaxed">
                  {generatedOfferLetter.text || JSON.stringify(generatedOfferLetter, null, 2)}
                </pre>
              )}
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="secondary" onClick={() => setShowOfferLetter(false)}>
                Close
              </Button>
              <Button onClick={() => { setShowOfferLetter(false); }}>
                <CheckCircle size={16} className="mr-1.5" />
                Looks Good
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Coach Modal - keeping existing implementation */}
      {editingCoach && (
        <Modal
          isOpen={!!editingCoach}
          onClose={() => setEditingCoach(null)}
          title="Edit Coach"
          size="lg"
        >
          <form onSubmit={handleEditCoach} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                <input
                  type="text"
                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:outline-none"
                  value={editingCoach.firstName || ''}
                  onChange={(e) => setEditingCoach({ ...editingCoach, firstName: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                <input
                  type="text"
                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:outline-none"
                  value={editingCoach.lastName || ''}
                  onChange={(e) => setEditingCoach({ ...editingCoach, lastName: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
              <input
                type="email"
                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:outline-none"
                value={editingCoach.email || ''}
                onChange={(e) => setEditingCoach({ ...editingCoach, email: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:outline-none"
                  value={editingCoach.phone || ''}
                  onChange={(e) => setEditingCoach({ ...editingCoach, phone: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Years of Experience</label>
                <input
                  type="number"
                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:outline-none"
                  value={editingCoach.yearsExperience || ''}
                  onChange={(e) => setEditingCoach({ ...editingCoach, yearsExperience: e.target.value })}
                />
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location <span className="pl-1 text-red-500">*</span></label>
              <select
                value={editingCoach.location}
                onChange={(e) => setEditingCoach({ ...editingCoach, location: e.target.value })}
                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:outline-none"
                required
              >
                <option value="">Select Location</option>
                {locations.map((loc) => {
                  const label = typeof loc === 'string' ? loc : loc.name || loc.label || loc;
                  return (
                    <option key={label} value={label}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>
            {/* Primary Sport */}
            <div>
              <Autocomplete
                label="Primary Sport "
                options={sports}
                value={editingCoach.primarySport}
                onChange={(value) => setEditingCoach({ ...editingCoach, primarySport: value })}
                placeholder="Select sport"
                getOptionLabel={(sport) => `${getSportIcon(sport)} ${sport}`}
                getOptionValue={(sport) => sport}
                required
              />
            </div>

            {/* Secondary Sports */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Secondary Sports</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {sports.filter(sport => normalizeSport(sport) !== normalizeSport(editingCoach.primarySport)).map((sport) => (
                  <button
                    key={sport}
                    type="button"
                    onClick={() => handleEditCoachSecondarySportToggle(sport)}
                    className={`p-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${editingCoach.secondarySports?.includes(sport)
                      ? 'bg-green-500 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    <span>{getSportIcon(sport)}</span>
                    <span>{sport}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Experience & Background</label>
              <textarea
                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:outline-none"
                value={editingCoach.experience || ''}
                onChange={(e) => setEditingCoach({ ...editingCoach, experience: e.target.value })}
                rows="3"
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t">
              <Button variant="secondary" onClick={() => setEditingCoach(null)}>
                Cancel
              </Button>
              <Button type="submit">Update Coach</Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Timesheets Modal - Review Style */}
      {/* <Modal
        isOpen={isTimesheetModalOpen}
        onClose={() => {
          setIsTimesheetModalOpen(false);
          setSelectedCoachForTimesheets(null);
          setTimesheets([]);
          setTimesheetSummary(null);
          setSelectedTimesheets([]);
          setIsRejectModalOpen(false);
          setRejectionReason("");
          setPayrollProfile(null);
          setAdminNotes('');
        }}
        title={selectedCoachForTimesheets ? `Timesheet Review - ${selectedCoachForTimesheets.firstName} ${selectedCoachForTimesheets.lastName}` : 'Timesheet Review'}
        size="xl"
      >
        {timesheetLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading timesheets...</p>
            </div>
          </div>
        ) : timesheets.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600 mb-2">No Timesheets Found</h3>
            <p className="text-gray-500">
              This coach hasn't submitted any timesheets yet.
            </p>
          </div>
        ) : (() => {
          const pendingEntries = timesheets.filter(ts => ts.status === 'submitted');
          const totalPendingHours = pendingEntries.reduce((sum, ts) => sum + (parseFloat(ts.totalHours) || 0), 0);
          const rate = payrollProfile?.hourlyRate || 0;
          const amount = totalPendingHours * rate;

          if (pendingEntries.length === 0) {
            return (
              <div className="text-center py-12">
                <CheckCircle className="h-16 w-16 text-green-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600 mb-2">All Timesheets Processed</h3>
                <p className="text-gray-500">
                  All timesheets for this coach have been reviewed.
                </p>
              </div>
            );
          }

          return (
            <div className="space-y-6">
            
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100">
                <h3 className="text-lg font-bold text-gray-900 mb-4 uppercase tracking-wide">Timesheet Review</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="font-semibold text-gray-700">Coach:</span>
                    <span className="ml-2 text-gray-900">
                      {selectedCoachForTimesheets?.firstName} {selectedCoachForTimesheets?.lastName}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">Job Title:</span>
                    <span className="ml-2 text-gray-900">
                      {payrollProfile?.jobTitle || 'Coach'}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">Employment:</span>
                    <span className="ml-2 text-gray-900">
                      {payrollProfile?.employmentType === 'volunteer' ? 'Volunteer' : 'Paid Coach'}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">Pay Period:</span>
                    <span className="ml-2 text-gray-900">{getPayPeriodRange(timesheets)}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-3 uppercase">Time Entries:</h4>
                <div className="space-y-2">
                  {pendingEntries.map((ts) => (
                    <div key={ts.id} className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-200 flex items-center justify-between text-sm">
                      <div className="flex items-center gap-4 flex-wrap">
                        <span className="font-medium text-gray-900 w-20 shrink-0">
                          {ts.date ? dayjs.utc(ts.date).format("MMM D") : ''}
                        </span>
                        <span className="text-gray-700 w-36 truncate shrink-0">{ts.location || 'N/A'}</span>
                        <span className="text-gray-600">
                          {ts.startTime || 'N/A'} – {ts.endTime || 'N/A'}
                        </span>
                      </div>
                      <span className="font-medium text-gray-900 shrink-0 ml-2">
                        {ts.totalHours || 0} hr{ts.totalHours !== 1 ? 's' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <hr className="border-gray-300" />

              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Total Hours:</span>
                  <span className="font-semibold text-gray-900">
                    {totalPendingHours.toFixed(1)} hour{totalPendingHours !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Hourly Rate:</span>
                  <span className="font-semibold text-gray-900">
                    {rate > 0 ? `$${rate.toFixed(2)} / hr` : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between items-center text-base border-t border-gray-200 pt-2 mt-2">
                  <span className="font-medium text-gray-800">Amount Owed:</span>
                  <span className="font-bold text-gray-900 text-lg">
                    {rate > 0 ? `$${amount.toFixed(2)}` : 'N/A'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Admin Notes:</label>
                <textarea
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none min-h-[80px] text-sm"
                  placeholder="Add notes about this timesheet..."
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  onClick={handleApproveAllTimesheets}
                  disabled={reviewActionLoading}
                  className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-semibold px-4 py-3 rounded-lg shadow-sm transition duration-200 text-sm"
                >
                  <CheckCircle className="h-4 w-4" />
                  {rate > 0 ? `APPROVE $${amount.toFixed(2)}` : 'APPROVE'}
                </button>
                <button
                  onClick={handleOpenRejectModalForReview}
                  disabled={reviewActionLoading}
                  className="flex-1 flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-semibold px-4 py-3 rounded-lg shadow-sm transition duration-200 text-sm"
                >
                  <XCircle className="h-4 w-4" />
                  Reject
                </button>
                <button
                  onClick={handleRequestCorrection}
                  disabled={reviewActionLoading}
                  className="flex-1 flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white font-semibold px-4 py-3 rounded-lg shadow-sm transition duration-200 text-sm"
                >
                  <Send className="h-4 w-4" />
                  Request Correction
                </button>
              </div>
            </div>
          );
        })()}
      </Modal> */}

      {/* Reset Password Modal */}
      <Modal
        isOpen={isResetPasswordModalOpen}
        onClose={() => {
          setIsResetPasswordModalOpen(false);
          setResetPasswordUser(null);
          setResetSent(false);
        }}
        title="Reset Password"
        size="sm"
      >
        {resetPasswordUser && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <Mail className="h-5 w-5 text-blue-400 mt-0.5 shrink-0" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Send Password Reset Link</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    A password reset email will be sent to:
                  </p>
                  <p className="text-sm font-semibold text-blue-800 mt-1 break-all">
                    {resetPasswordUser.email}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    User: {resetPasswordUser.firstName} {resetPasswordUser.lastName}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-sm text-gray-600">
                Firebase will send an email with a password reset link. The user can click the link to set a new password. This works for all user roles without restrictions.
              </p>
            </div>

            {resetSent ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-green-800 font-medium">Reset link sent successfully!</p>
                <p className="text-green-600 text-sm mt-1">The user will receive the email shortly.</p>
              </div>
            ) : (
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setIsResetPasswordModalOpen(false);
                    setResetPasswordUser(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => handleSendResetLink(resetPasswordUser)}
                  disabled={resetSending}
                >
                  {resetSending ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Approve Coach Confirmation Modal */}
      <Modal
        isOpen={isApproveModalOpen}
        onClose={() => { setIsApproveModalOpen(false); setApproveCoachTarget(null); }}
        title="Approve Coach Account"
        size="sm"
      >
        {approveCoachTarget && (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="flex items-start">
                <UserCheck className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-emerald-800">
                    Approve {approveCoachTarget.firstName} {approveCoachTarget.lastName}?
                  </h3>
                  <p className="text-sm text-emerald-700 mt-1">
                    They will be able to sign in to the coach portal.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-sm text-gray-600">
                A payroll profile will be auto-created for this coach after approval.
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                variant="secondary"
                onClick={() => { setIsApproveModalOpen(false); setApproveCoachTarget(null); }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmApproveCoach}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Approve
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Reject Modal for Timesheets */}
      {isRejectModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Reject Timesheets</h2>
            <p className="text-sm text-gray-600 mb-4">
              You are about to reject {timesheets.filter(ts => ts.status === 'submitted').length} timesheet(s). Please provide a reason for rejection.
            </p>

            <div className="flex flex-col mb-4">
              <label className="text-sm font-medium text-gray-700 mb-1">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 min-h-[100px]"
                placeholder="Enter rejection reason..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                onClick={() => {
                  setIsRejectModalOpen(false);
                  setRejectionReason("");
                }}
                disabled={reviewActionLoading}
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectAllTimesheets}
                disabled={reviewActionLoading || !rejectionReason.trim()}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {reviewActionLoading ? "Rejecting..." : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      <Modal isOpen={showProfile} onClose={handleCloseProfile} title="Coach Profile" size="lg">
        {profileCoach && (
          <div className="space-y-4">
            {/* Profile Tab Bar */}
            <div className="flex gap-0 border-b border-gray-200">
              {['Information', 'Onboarding', 'Agreements', 'Courses', 'Quizzes'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveProfileTab(tab)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeProfileTab === tab
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Information Tab */}
            {activeProfileTab === 'Information' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { label: 'First Name', value: profileCoach.firstName },
                  { label: 'Last Name', value: profileCoach.lastName },
                  { label: 'Email', value: profileCoach.email },
                  { label: 'Phone', value: profileCoach.phone },
                  { label: 'Sport(s)', value: profileCoach.primarySport || (profileCoach.secondarySports || []).join(', ') || '—' },
                  { label: 'Location(s)', value: profileCoach.location || (profileCoach.assignedLocations || []).join(', ') || '—' },
                  { label: 'Position', value: profileCoach.coachPosition || '—' },
                  { label: 'Employment Type', value: profileCoach.employmentType || '—' },
                  { label: 'Rate', value: profileCoach.hourlyPayRate ? `$${profileCoach.hourlyPayRate}/hr` : '—' },
                  { label: 'Registration Source', value: profileCoach.registrationSource || '—' },
                  { label: 'Joined', value: profileCoach.createdAt ? (
                    profileCoach.createdAt.toDate ?
                      profileCoach.createdAt.toDate().toLocaleDateString() :
                      new Date(profileCoach.createdAt).toLocaleDateString()
                  ) : 'N/A' },
                  { label: 'Coach ID', value: profileCoach.id },
                ].map((field) => (
                  <div key={field.label} className="bg-gray-50 rounded-lg p-3">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">{field.label}</label>
                    <p className="mt-1 text-sm text-gray-900 break-words">{field.value}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Onboarding Tab */}
            {activeProfileTab === 'Onboarding' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Onboarding Status</label>
                    <div className="mt-2">
                      {profileCoach.onboardStatus === 'completed' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                          <CheckCircle size={14} /> Completed
                        </span>
                      ) : profileCoach.onboardStatus === 'invited' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <Send size={14} /> Invited
                        </span>
                      ) : profileCoach.onboardStatus === 'in_progress' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          In Progress
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {profileCoach.onboardStatus || 'Not Started'}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Approval Status</label>
                    <div className="mt-2">
                      {profileCoach.isActive === false || profileCoach.approvalStatus === 'rejected' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <XCircle size={14} /> Rejected
                        </span>
                      ) : profileCoach.approvalStatus === 'pending' || profileCoach.status === 'pending' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          Pending
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                          <UserCheck size={14} /> Approved
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Registration Source</label>
                    <p className="mt-1 text-sm text-gray-900 capitalize">{profileCoach.registrationSource || '—'}</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Courses Assigned</label>
                    <p className="mt-1 text-sm text-gray-900">{(profileCoach.assignedCourses || []).length} course(s)</p>
                   
                  </div>
                </div>

                {profileCoach.registrationSource === 'coach_portal' && profileCoach.isActive === false && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />nbvbnvvvvv
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-amber-800">Pending Approval</h3>
                        <p className="text-sm text-amber-700 mt-1">
                          This coach registered through the portal and needs to be approved.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Agreements Tab */}
            {activeProfileTab === 'Agreements' && (
              <div className="bg-gray-50 rounded-lg p-6 text-center">
                <FileText className="mx-auto h-10 w-10 text-gray-400" />
                <h3 className="mt-3 text-sm font-medium text-gray-900">Agreements</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Agreement data integration coming soon.
                </p>
              </div>
            )}

            {/* Courses Tab */}
            {activeProfileTab === 'Courses' && (
              <div className="space-y-5">
                {(() => {
                  const assignedIds = profileCoach.assignedCourses || [];
                  const assignedCourses = assignedIds
                    .map((id) => allCourses.find((c) => c.id === id))
                    .filter(Boolean);
                  return (
                    <>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-800 mb-3">Current Courses</h4>
                        {assignedCourses.length === 0 ? (
                          <div className="bg-gray-50 rounded-lg p-4 text-center">
                            <BookOpen size={24} className="mx-auto text-gray-300 mb-1" />
                            <p className="text-sm text-gray-500">No courses assigned yet.</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {assignedCourses.map((course) => {
                              const results = coachQuizResults.filter((r) => r.courseTitle === course.title);
                              const completed = results.some((r) => r.passed);
                              const inProgress = results.length > 0 && !completed;
                              let statusText = 'Pending';
                              let statusColor = 'bg-gray-100 text-gray-700';
                              if (completed) {
                                statusText = 'Completed';
                                statusColor = 'bg-emerald-100 text-emerald-800';
                              } else if (inProgress) {
                                statusText = 'In Progress';
                                statusColor = 'bg-amber-100 text-amber-800';
                              }
                              return (
                                <div key={course.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <BookOpen size={18} className="text-gray-400" />
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">{course.title}</p>
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
                                        {statusText}
                                      </span>
                                    </div>
                                  </div>
                                  {completed && (
                                    <button
                                      onClick={() => {
                                        setPendingQuizCourse(course.title);
                                        setActiveProfileTab('Quizzes');
                                      }}
                                      className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
                                    >
                                      View Result
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div className="border-t border-gray-200 pt-5">
                        <button
                          onClick={() => setShowCourseEditor(!showCourseEditor)}
                          className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-3 hover:text-indigo-600 transition"
                        >
                          {showCourseEditor ? '▼' : '▶'} Manage Courses
                        </button>
                        {showCourseEditor && (
                        allCourses.length === 0 ? (
                          <div className="text-center py-6 text-gray-500">
                            <p className="text-sm">No courses available.</p>
                          </div>
                        ) : (
                          <>
                            <div className="space-y-2 max-h-56 overflow-y-auto border border-gray-200 rounded-xl p-3">
                              {allCourses.map((course) => {
                                const isSelected = selectedCourseIds.includes(course.id);
                                return (
                                  <label
                                    key={course.id}
                                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all text-sm ${
                                      isSelected ? 'bg-indigo-100 border border-indigo-300' : 'bg-white border border-gray-200 hover:bg-gray-50'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => {
                                        setSelectedCourseIds((prev) =>
                                          isSelected ? prev.filter((id) => id !== course.id) : [...prev, course.id]
                                        );
                                      }}
                                      className="h-4 w-4 text-indigo-600 rounded border-gray-300"
                                    />
                                    <span className="font-medium text-gray-900 truncate">{course.title}</span>
                                    {course.isRequired && (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700 shrink-0">
                                        Required
                                      </span>
                                    )}
                                  </label>
                                );
                              })}
                            </div>
                            {selectedCourseIds.length > 0 && (
                              <p className="text-xs text-gray-500 mt-2">{selectedCourseIds.length} course(s) selected</p>
                            )}
                            <div className="flex justify-end gap-3 mt-4">
                              <button
                                onClick={() => setSelectedCourseIds(profileCoach.assignedCourses || [])}
                                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                              >
                                Reset
                              </button>
                              <button
                                onClick={async () => {
                                  if (!profileCoach) return;
                                  try {
                                    setCourseAssignmentSaving(true);
                                    await updateCoach(profileCoach.id, {
                                      assignedCourses: selectedCourseIds
                                    });
                                    setProfileCoach({ ...profileCoach, assignedCourses: selectedCourseIds });
                                    success('Courses updated successfully!');
                                  } catch (err) {
                                    console.error('Failed to update courses:', err);
                                    error('Failed to update courses: ' + err.message);
                                  } finally {
                                    setCourseAssignmentSaving(false);
                                  }
                                }}
                                disabled={courseAssignmentSaving}
                                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition flex items-center gap-1.5 disabled:opacity-50"
                              >
                                {courseAssignmentSaving ? (
                                  <><Loader2 size={14} className="animate-spin" /> Saving...</>
                                ) : (
                                  <><BookOpen size={14} /> Save Courses</>
                                )}
                              </button>
                            </div>
                          </>
                        )
                      )}
                    </div>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Quizzes Tab */}
            {activeProfileTab === 'Quizzes' && (
              <div>
                {(() => {
                  if (pendingQuizCourse) {
                    setSelectedQuizCourse(pendingQuizCourse);
                    setPendingQuizCourse('');
                  }
                  const assignedIds = profileCoach.assignedCourses || [];
                  const assignedCourses = assignedIds
                    .map((id) => allCourses.find((c) => c.id === id))
                    .filter(Boolean)
                    .map((c) => c.title);
                  const hasNoResults = coachQuizResults.length === 0;
                  const filtered = selectedQuizCourse
                    ? coachQuizResults.filter(r => r.courseTitle === selectedQuizCourse)
                    : coachQuizResults;
                  const selectedCourseHasResults = !selectedQuizCourse || filtered.length > 0;
                  return (
                    <>
                      <div className="mb-4">
                        <select
                          value={selectedQuizCourse}
                          onChange={(e) => setSelectedQuizCourse(e.target.value)}
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="">All Courses</option>
                          {assignedCourses.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                      {hasNoResults && !selectedQuizCourse ? (
                        <div className="bg-gray-50 rounded-lg p-6 text-center">
                          <BookOpen className="mx-auto h-10 w-10 text-gray-400" />
                          <h3 className="mt-3 text-sm font-medium text-gray-900">No Quiz Results</h3>
                          <p className="mt-1 text-sm text-gray-500">
                            This coach has not completed any quizzes yet.
                          </p>
                        </div>
                      ) : !selectedCourseHasResults ? (
                        <div className="bg-gray-50 rounded-lg p-6 text-center">
                          <BookOpen className="mx-auto h-10 w-10 text-gray-400" />
                          <h3 className="mt-3 text-sm font-medium text-gray-900">No Task Given</h3>
                          <p className="mt-1 text-sm text-gray-500">
                            No quiz results found for the selected course.
                          </p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Attempt</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Passing</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {filtered.map((result, idx) => {
                                const dateStr = result.attemptedAt?.toDate
                                  ? result.attemptedAt.toDate().toLocaleDateString()
                                  : result.attemptedAt
                                    ? new Date(result.attemptedAt).toLocaleDateString()
                                    : '—';
                                const mins = Math.floor((result.timeSpent || 0) / 60);
                                const secs = (result.timeSpent || 0) % 60;
                                const timeStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
                                return (
                                  <tr key={result.id || idx} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 text-sm text-gray-900">{result.courseTitle || '—'}</td>
                                    <td className="px-3 py-2 text-sm text-gray-600">{idx + 1}</td>
                                    <td className="px-3 py-2 text-sm text-gray-600">{dateStr}</td>
                                    <td className="px-3 py-2 text-sm text-gray-900 font-medium">{result.score}/{result.total} ({result.percentage}%)</td>
                                    <td className="px-3 py-2 text-sm text-gray-600">{result.percentage >= 80 ? '≥80%' : '<80%'}</td>
                                    <td className="px-3 py-2 text-sm text-gray-600">{timeStr}</td>
                                    <td className="px-3 py-2">
                                      {result.passed ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                          <CheckCircle size={12} /> Passed
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                          <XCircle size={12} /> Failed
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </Modal>

      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((t) => (
          <Toast
            key={t.id}
            message={t.message}
            type={t.type}
            duration={t.duration}
            onClose={() => removeToast(t.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default Coaches;